import React, { useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toLocationString } from "../hooks/useRoutePoints";
import { appendTravelModeToGoogleMapsUrl } from "../utils/googleMaps";

export default function RouteMap(props) {
  const { show, onHide, routeResult, origin, destination, points, vehicleType } = props;
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const overlaysRef = useRef([]);

  const mainItem = routeResult && routeResult.items ? routeResult.items[0] : null;
  const totalDistance = mainItem && mainItem.distance ? mainItem.distance.text : null;
  const totalDuration = mainItem && mainItem.duration ? mainItem.duration.text : null;
  const routeDescription = mainItem ? mainItem.description : null;

  let googleMapsUrl = null;
  if (origin && origin.location && destination && destination.location) {
    const enc = function (loc) {
      return encodeURIComponent(loc);
    };

    // Prioriza as coordenadas que o BACKEND já resolveu
    const resolvedOrigin =
      mainItem?.origin?.latitude != null && mainItem?.origin?.longitude != null
        ? `${mainItem.origin.latitude},${mainItem.origin.longitude}`
        : toLocationString(origin);

    const resolvedDestination =
      mainItem?.destination?.latitude != null && mainItem?.destination?.longitude != null
        ? `${mainItem.destination.latitude},${mainItem.destination.longitude}`
        : toLocationString(destination);

    let wp = "";
    if (points && points.length) {
      const parts = points.map(function (p) {
        return enc(toLocationString(p));
      });
      wp = "&waypoints=" + parts.join("|");
    }

    googleMapsUrl =
      "https://www.google.com/maps/dir/?api=1&origin=" +
      enc(resolvedOrigin) +
      "&destination=" +
      enc(resolvedDestination) +
      wp;

    googleMapsUrl = appendTravelModeToGoogleMapsUrl(googleMapsUrl, vehicleType);
  }

  const clearOverlays = function () {
    overlaysRef.current.forEach(function (o) {
      o.setMap(null);
    });
    overlaysRef.current = [];
  };

  useEffect(() => {
    if (!show || !mainItem || !mainItem.encodedPolyline) return;

    const init = () => {
      if (!mapRef.current || !window.google?.maps?.geometry?.encoding) {
        setTimeout(init, 150);
        return;
      }

      if (!mapInstance.current) {
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: -23.55, lng: -46.63 },
          zoom: 11,
        });
      } else {
        window.google.maps.event.trigger(mapInstance.current, "resize");
      }

      clearOverlays();

      const path = window.google.maps.geometry.encoding.decodePath(mainItem.encodedPolyline);

      const polyline = new window.google.maps.Polyline({
        path: path,
        geodesic: true,
        strokeColor: "#4285F4",
        strokeOpacity: 1,
        strokeWeight: 4,
        map: mapInstance.current,
      });

      overlaysRef.current.push(polyline);

      const bounds = new window.google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));

      const addMarker = (lat, lng, label, color) => {
        if (lat == null || lng == null) return;

        const pos = { lat, lng };

        const marker = new window.google.maps.Marker({
          position: pos,
          map: mapInstance.current,
          label: {
            text: label,
            color: "#fff",
            fontWeight: "700",
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: "#fff",
            strokeWeight: 2,
          },
        });

        overlaysRef.current.push(marker);
        bounds.extend(pos);
      };

      addMarker(
        mainItem.origin?.latitude,
        mainItem.origin?.longitude,
        "O",
        "#16a34a"
      );

      addMarker(
        mainItem.destination?.latitude,
        mainItem.destination?.longitude,
        "D",
        "#dc2626"
      );

      mapInstance.current.fitBounds(bounds);
    };

    setTimeout(init, 250);
  }, [show, mainItem]);

  useEffect(() => {
    if (!show) {
      clearOverlays();
      mapInstance.current = null;
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} fullscreen>
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-3 flex-wrap w-100">
          <Modal.Title style={{ fontSize: 16 }}>
            {t("ROUTING.VIEW_MAP")}
          </Modal.Title>

          {mainItem && (
            <div className="d-flex gap-3 ms-2" style={{ fontSize: 13 }}>
              {totalDistance && <span className="badge bg-secondary">{totalDistance}</span>}
              {totalDuration && <span className="badge bg-secondary">{totalDuration}</span>}
              {routeDescription && (
                <span className="text-muted" style={{ fontSize: 12 }}>
                  via {routeDescription}
                </span>
              )}
            </div>
          )}

          {googleMapsUrl && (
            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary btn-sm ms-auto me-2"
              style={{ fontSize: 12 }}
            >
              Abrir no Google Maps
            </a>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="p-0" style={{ position: "relative" }}>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      </Modal.Body>
    </Modal>
  );
}
